<template>
  <div class="column full-width full-height no-wrap bg-white">
    <!-- The container tracking the resize must have overflow-hidden so the canvas can't stretch it -->
    <div
      ref="containerRef"
      class="canvas-container col-grow relative-position overflow-hidden bg-grey-2"
    >
      <!-- NEW: Floating Undo Button & Tutorial -->
      <div class="absolute-top-left q-pa-sm row items-center no-wrap" style="z-index: 10">
        <q-btn
          round
          dense
          color="white"
          text-color="primary"
          icon="undo"
          :loading="isUndoing"
          :disable="!hits.length"
          class="shadow-2"
        >
          <q-menu anchor="bottom left" self="top left">
            <q-card class="q-pa-sm" style="min-width: 200px">
              <div class="text-caption q-mb-sm">Undo this hit? This can't be undone.</div>
              <div class="row justify-end q-gutter-xs">
                <q-btn flat dense label="Cancel" color="grey-7" v-close-popup />
                <q-btn flat dense label="Undo" color="negative" v-close-popup @click="handleUndo" />
              </div>
            </q-card>
          </q-menu>
          <HelpHint text="Undo the hit." />
        </q-btn>

      </div>
      <!-- FLOATING HUD: Tutorial Overlay -->
      <div
        v-if="showTutorial && tutorialText !== ''"
        class="q-ml-sm absolute-bottom-right"
        style="pointer-events: none"
      >
        <q-chip
          color="dark"
          text-color="white"
          icon="touch_app"
          size="sm"
          class="shadow-2"
          style="pointer-events: auto"
        >
          {{ tutorialText }}
          <q-icon
            name="close"
            class="q-ml-sm cursor-pointer"
            @click.stop="showTutorial = false"
          />
        </q-chip>
      </div>
      <!-- NEW: Shift Defense Mode Toggle -->
      <div class="absolute-top-right q-pa-sm" style="z-index: 10">
        <q-btn
          round
          dense
          :color="adjustDefenseMode ? 'primary' : 'white'"
          :text-color="adjustDefenseMode ? 'white' : 'primary'"
          icon="back_hand"
          class="shadow-2"
          @click="adjustDefenseMode = !adjustDefenseMode"
        >
          <HelpHint text="Toggle Shift Defense Mode to drag fielders" />
        </q-btn>
      </div>

      <v-stage
        class="absolute-top-left"
        v-if="stageConfig.width > 0"
        :config="{
          ...stageConfig,
          onClick: handleTap,
          onTap: handleTap,
        }"
      >
        <v-layer>
          <!-- Base Field Layer (Sourced from helpers.ts) -->
          <v-rect :config="backgroundConfig" />
          <v-wedge :config="outfieldWedgeConfig" />
          <v-wedge :config="infieldDirtConfig" />
          <v-regular-polygon :config="diamondConfig" />
          <v-regular-polygon :config="homePlateConfig" />
          <v-regular-polygon :config="firstBaseConfig" />
          <v-regular-polygon :config="secondBaseConfig" />
          <v-regular-polygon :config="thirdBaseConfig" />

          <!-- RETROSHEET LAYOUT MODIFIERS -->
          <v-line :config="retroLineLCConfig" />
          <v-line :config="retroLineRCConfig" />
          <v-arc :config="retroShortArcConfig" />
          <v-line :config="retroLineLCConfig" />

          <!-- Plotted Hit Lines (Dashed based on trajectory) -->
          <v-line
            v-for="hit in hits"
            :key="'line-' + hit.id"
            :config="{
              points: [homeAnchorX, homeAnchorY, hit.x, hit.y],
              stroke: hit.type === 'out' ? '#e74c3c' : '#3498db',
              strokeWidth: 2,
              dash: getTrajectoryDash(hit.trajectory),
            }"
          />

          <!-- Plotted Hit Markers -->
          <v-circle
            v-for="hit in hits"
            :key="'marker-' + hit.id"
            :config="{
              x: hit.x,
              y: hit.y,
              radius: 6,
              fill: hit.type === 'out' ? '#e74c3c' : '#3498db',
              stroke: '#2c3e50',
              strokeWidth: 1.5,
            }"
          />

          <!-- Active Runners on Field (Locked) -->
          <v-group :config="{ listening: false }">
            <FieldPlayerMarker
              v-for="runner in fieldRunners"
              :key="'runner-canvas-' + runner.id"
              type="runner"
              :uniform-number="runner.uniformNumber ?? null"
              :x="runner.x"
              :y="runner.y"
              :opacity="fieldMarkerOpacity"
              :show-position-initials="true"
            />
          </v-group>

          <!-- Plotted Defensive Fielders -->
          <!-- The outer group dynamically activates listening mode -->
          <!-- The outer group dynamically activates listening mode -->
          <v-group :config="{ listening: adjustDefenseMode }">

            <!-- Render FieldPlayerMarkers directly. They will handle their own dragging! -->
            <FieldPlayerMarker
              v-for="fielder in fielders"
              :key="'fielder-' + fielder.position"
              type="fielder"
              :uniform-number="fielder.uniformNumber"
              :position="fielder.position"
              :x="fielder.x"
              :y="fielder.y"
              :opacity="adjustDefenseMode ? 0.8 : fieldMarkerOpacity"
              :show-zone="true"
              :show-position-initials="false"
              :zone-radius="fielder.zoneRadius"
              :draggable="adjustDefenseMode"
              @dragend="onFielderDragEnd(fielder.position, $event)"
            />

          </v-group>
        </v-layer>
      </v-stage>
      <div class="absolute-bottom-right q-pa-sm" style="z-index: 10">
        <q-btn-toggle
          v-model="adjustDefenseMode"
          rounded
          unelevated
          toggle-color="primary"
          color="white"
          text-color="grey-8"
          :options="[
            { label: 'Hit', value: false, icon: 'sports_baseball' },
            { label: 'Fielders', value: true, icon: 'shield' },
          ]"
          class="shadow-3 text-weight-bold"
          size="sm"
        />
      </div>
    </div>

    <!-- SLIM ACTION BAR: Maximizes Canvas Space -->
    <q-card flat class="shrink shadow-up-2">
      <q-card-section class="q-py-sm">
        <!-- Row 1: Trajectories (Compact No-Wrap Flex) -->
        <div class="row q-gutter-x-xs q-mb-sm no-wrap">
          <q-btn
            v-for="cat in settingsStore.primaryFieldingTrajectories?.filter(
              (c) => c.visible !== false,
            )"
            :key="cat.value"
            :icon="cat.icon"
            :label="cat.label"
            :color="cat.color"
            class="col text-weight-bold"
            unelevated
            size="sm"
            padding="xs"
            :outline="hitTrajectory !== cat.value"
            @click="handleTrajectoryChange(cat.value)"
          >
            <q-tooltip v-if="cat.tooltip" class="bg-accent">{{ cat.tooltip }}</q-tooltip>
          </q-btn>
        </div>

        <!-- Pitch Quality -->
        <div class="row q-gutter-x-xs q-mb-sm no-wrap">
          <q-btn
            v-for="q in settingsStore.primaryHitQualityStates"
            :key="q.value"
            :label="q.label"
            :color="hitQuality === q.value ? 'warning' : 'grey-2'"
            :text-color="hitQuality === q.value ? 'white' : 'black'"
            class="col text-weight-bold"
            unelevated
            size="sm"
            padding="xs"
            @click="handleHitQuality(q.value)"
          />
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import { useFieldLayout } from 'src/helpers/helpers'
import { useAsyncGuard } from 'src/composables/useAsyncGuard'
import FieldPlayerMarker from 'components/FieldPlayerMarker.vue'
import HelpHint from 'components/HelpHint.vue'
import type { PitchTransaction } from 'src/stores/base-types'

const gameStore = useGameStore()
const settingsStore = useSettingsStore()
const emit = defineEmits(['switch-view'])

// --- NEW: ADJUST DEFENSE STATE ---
const adjustDefenseMode = ref(false)

onMounted(() => {
  if (containerRef.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        stageConfig.width = entry.contentRect.width
        stageConfig.height = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

onBeforeUnmount(() => {
  if (resizeObserver && containerRef.value) {
    resizeObserver.unobserve(containerRef.value)
  }
})
// --- RESPONSIVE CANVAS SETUP ---
const containerRef = ref<HTMLElement | null>(null)
const stageConfig = reactive({ width: 0, height: 0 })
let resizeObserver: ResizeObserver | null = null

const stageSizeRef = computed(() => ({ width: stageConfig.width, height: stageConfig.height }))
const {
  homeAnchorX,
  homeAnchorY,
  fieldScale,
  backgroundConfig,
  outfieldWedgeConfig,
  infieldDirtConfig,
  diamondConfig,
  homePlateConfig,
  firstBaseConfig,
  secondBaseConfig,
  thirdBaseConfig,
  retroLineLCConfig,
  retroLineRCConfig,
  retroShortArcConfig,
  getTrajectoryDash,
  getStandardPositionCoords,
  getPositionCircle,
} = useFieldLayout(stageSizeRef)

const fieldMarkerOpacity = 0.4
const showTutorial = ref(true)

const tutorialText = computed(() => {
  if (!hits.value.length) {
    return '① Tap the field where the ball was hit.'
  }
  if (!hitTrajectory.value) {
    return '③ Select the ball trajectory.'
  }
  return ''
})

// Will seamlessly return the exact pitch being edited, OR default to the very last pitch thrown!
const activeEditPitch = computed(() => {
  const currentTxns = gameStore.currentAtBatTransactions || []
  const pitch = currentTxns.find((p) => p.id === gameStore.editingPitchId)
  return pitch ? pitch : currentTxns[currentTxns.length - 1]
})

// --- TRANSACTION UPDATERS ---
const promptHitDetails = async (hitData: {
  relX: number | null
  relY: number | null
  trajectory: string | null
  hitQualityRating?: number | null
  zoneFielderPosition?: number | null
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateData: any = {
    hitLocationX: hitData.relX,
    hitLocationY: hitData.relY,
    battedBallTrajectory: hitData.trajectory,
    hitQualityRating: hitData.hitQualityRating,
  }

  // Bind the zone opportunity to the database record!
  if (hitData.zoneFielderPosition !== undefined) {
    updateData.zoneFielderPosition = hitData.zoneFielderPosition
  }

  // Live Mode OR Edit mode handled safely by our robust computed variable
  if (activeEditPitch.value) {
    await gameStore.updatePitch(activeEditPitch.value.id, updateData)
  }
}

const updateHitTrajectory = async (trajectory: string) => {
  if (activeEditPitch.value) {
    await gameStore.updatePitch(activeEditPitch.value.id, { battedBallTrajectory: trajectory })
  }
}

// --- UI STATE MODELS (Q-BTN-TOGGLES) ---
const localTrajectory = ref<string | null>(null)
const hitTrajectory = computed<string | null>({
  get() {
    if (!activeEditPitch.value) return null
    const txn = gameStore.transactions.find(
      (t) => String(t.id) === String(activeEditPitch.value!.id),
    ) as PitchTransaction
    return txn?.battedBallTrajectory || null
  },
  set(value) {
    localTrajectory.value = value
  },
})
const localhitQuality = ref<number | null>(null)
const hitQuality = computed<number | null>({
  get() {
    if (!activeEditPitch.value) return null
    const txn = gameStore.transactions.find(
      (t) => String(t.id) === String(activeEditPitch.value!.id),
    ) as PitchTransaction
    return txn?.hitQualityRating || null
  },
  set(value) {
    localhitQuality.value = value
  },
})

// --- WIZARD AUTO-ADVANCE LOGIC ---
const checkAndAdvance = () => {
  // Only auto-advance the wizard once BOTH fields are filled out
  if (hitTrajectory.value && hits.value.length >= 1 && hitQuality.value) {
    emit('switch-view', 'play')
  }
}

// --- INTERACTION EVENT LOGIC ---
const handleTrajectoryChange = async (val: string) => {
  await updateHitTrajectory(val)
  checkAndAdvance()
}

const handleHitQuality = async (rating: number) => {
  if (activeEditPitch.value) {
    await gameStore.updatePitch(activeEditPitch.value.id, { hitQualityRating: rating })
    checkAndAdvance() // <-- Add this so it triggers the teleport!
  }
}

const { isPending: isUndoing, guard: guardUndo } = useAsyncGuard()
const handleUndo = guardUndo(async () => {
  if (activeEditPitch.value) {
    await gameStore.updatePitch(activeEditPitch.value.id, {
      hitLocationX: null,
      hitLocationY: null,
      hitQualityRating: null,
      battedBallTrajectory: null,
    })
  }
})

// Dynamically scale the radius relative to the physical drawn field scale
const getFielderZoneRadius = (position: number) => {
  const scale = fieldScale.value
  switch (position) {
    case 7:
    case 8:
    case 9:
      return scale * settingsStore.fielderZoneScale[7]!
    case 4:
      return scale * settingsStore.fielderZoneScale[6]!
    case 6:
      return scale * settingsStore.fielderZoneScale[6]!
    case 3:
      return scale * settingsStore.fielderZoneScale[3]!
    case 5:
      return scale * settingsStore.fielderZoneScale[5]!
    case 1:
      return scale * settingsStore.fielderZoneScale[1]!
    case 2:
      return scale * settingsStore.fielderZoneScale[2]!
    default:
      return scale * 0.15
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleTap = async (e: any) => {
  // NEW: Safeguard against logging hits while moving fielders around
  if (adjustDefenseMode.value) return

  if (e.evt && e.evt.preventDefault) e.evt.preventDefault()

  const stage = e.target.getStage()
  if (!stage) return

  const pointerPosition = stage.getPointerPosition()
  if (!pointerPosition) return

  // Calculate the offset from Home Plate, normalized by the scale of the field
  const relX = parseFloat(((pointerPosition.x - homeAnchorX.value) / fieldScale.value).toFixed(4))
  const relY = parseFloat(((pointerPosition.y - homeAnchorY.value) / fieldScale.value).toFixed(4))

  // NEW: Calculate distance to fielders to attribute DZR/UZR fielding opportunities
  let zoneFielderPosition: number | null = null
  let closestDist = Infinity

  for (const fielder of fielders.value) {
    const dist = Math.sqrt(
      Math.pow(pointerPosition.x - fielder.x, 2) + Math.pow(pointerPosition.y - fielder.y, 2),
    )
    const fielderRadius = getFielderZoneRadius(fielder.position)

    if (dist <= fielderRadius && dist < closestDist) {
      closestDist = dist
      zoneFielderPosition = fielder.position
    }
  }

  await promptHitDetails({
    relX,
    relY,
    trajectory: hitTrajectory.value, // Reads our computed getter
    hitQualityRating: hitQuality.value,
    zoneFielderPosition,
  })
  checkAndAdvance()
}

// --- FIELDER DRAG HANDLER ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onFielderDragEnd = async (position: number, e: any) => {
  if (!adjustDefenseMode.value) return

  // e.target is the physical Konva Node (the <v-group>) that was just dragged.
  const node = e.target

  if (!node) return

  // Grab the exact X and Y coordinates where the node was dropped
  const droppedX = node.x()
  const droppedY = node.y()

  // Convert the physical drop location back into raw relative coordinates
  const relX = parseFloat(((droppedX - homeAnchorX.value) / fieldScale.value).toFixed(4))
  const relY = parseFloat(((droppedY - homeAnchorY.value) / fieldScale.value).toFixed(4))

  // Issue the update to the GameStore so it permanently anchors!
  await gameStore.updateFielderAlignment(position, relX, relY)
}

// --- GRAPHICAL DATA MAPPING ---
const fielders = computed(() => {
  const fielderArray = []

  // Reads from the active defensive map so dragged coords override defaults
  for (const fielder of gameStore.currentDefence) {
    if (fielder.position >= 1 && fielder.position <= 9) {
      let x, y
      // Map stored custom relative coords OR fallback to default geometry
      if (fielder.relX !== undefined && fielder.relY !== undefined) {
        x = homeAnchorX.value + fielder.relX * fieldScale.value
        y = homeAnchorY.value + fielder.relY * fieldScale.value
      } else {
        const fielderCords = getStandardPositionCoords(fielder.position)
        x = fielderCords.x
        y = fielderCords.y
      }

      fielderArray.push({
        type: 'fielder',
        uniformNumber: fielder.uniformNumber || 0,
        position: fielder.position,
        x,
        y,
        zoneRadius: getPositionCircle(fielder.position).radius,
      })
    }
  }

  // Edge case fallback
  if (fielderArray.length === 0) {
    for (let i = 1; i <= 9; i++) {
      const coords = getStandardPositionCoords(i)
      fielderArray.push({
        type: 'fielder',
        uniformNumber: i,
        position: i,
        x: coords.x,
        y: coords.y,
        zoneRadius: getFielderZoneRadius(i),
      })
    }
  }

  return fielderArray
})

// LIVE SPRAY CHART LOGIC
const hits = computed(() => {
  const currentPA = gameStore.plateAppearances[gameStore.currentPAIndex]
  if (!currentPA || !currentPA.batterId) return []

  return gameStore.transactions
    .filter(
      (t) =>
        t.plateAppearanceId === currentPA.id &&
        (t as PitchTransaction).hitLocationX != null &&
        (t as PitchTransaction).hitLocationY != null,
    )
    .map((t) => {
      const pitchTxn = t as PitchTransaction
      const outcome = pitchTxn.outcome || pitchTxn.pitchOutcome || ''
      const isHit = ['S', 'D', 'T', 'HR', 'X'].includes(outcome)

      return {
        id: pitchTxn.id,
        // Base X and Y dynamically off home plate and the field scale
        x: homeAnchorX.value + (pitchTxn.hitLocationX || 0) * fieldScale.value,
        y: homeAnchorY.value + (pitchTxn.hitLocationY || 0) * fieldScale.value,
        relX: pitchTxn.hitLocationX,
        relY: pitchTxn.hitLocationY,
        type: isHit ? 'hit' : 'out',
        trajectory: pitchTxn.battedBallTrajectory || 'G',
        hitQualityRating: pitchTxn.hitQualityRating,
      }
    })
})

const fieldRunners = computed(() => {
  const currentBaseRunners = gameStore.currentBaseRunners
  const runners: Array<{
    id: number
    x: number
    y: number
    uniformNumber?: number | string | null
  }> = []

  const basesMap = {
    '1': firstBaseConfig,
    '2': secondBaseConfig,
    '3': thirdBaseConfig,
    B: homePlateConfig,
  }

  for (const baseKey of ['1', '2', '3', 'B']) {
    const rList = currentBaseRunners[baseKey as keyof typeof currentBaseRunners] || []
    const baseCoords = basesMap[baseKey as keyof typeof basesMap]

    rList.forEach((runner, index) => {
      runners.push({
        ...runner,
        x: baseCoords.value.x + index * 20,
        y: baseCoords.value.y - index * 20,
      })
    })
  }

  return runners
})
</script>

<style scoped>
.canvas-container {
  /* Removed border and margins to let the canvas push to the literal edges of the screen */
  border: none;
  border-radius: 0px;
  margin: 0px;
  box-sizing: border-box;
  min-height: 200px;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #ccc;
}
</style>
